import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminLogin, adminStats, adminOrders, adminSubscribers, adminMessages,
  getProducts, getJournal, adminDeleteProduct, adminDeleteArticle, api,
} from "@/lib/api";
import { toast } from "sonner";
import { LogOut, BookOpen, Feather, Users, Mail, Coins, ShoppingBag, Trash2, Upload, Sparkles, CreditCard, CheckCircle2, AlertTriangle, Copy } from "lucide-react";

function Login({ onIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin({ email, password });
      localStorage.setItem("aw_token", res.token);
      onIn();
    } catch { toast.error("Invalid email or password"); }
    finally { setLoading(false); }
  };
  const cls = "w-full bg-brand-warm-white border border-brand-linen px-5 py-4 font-body focus:outline-none focus:border-brand-gold";
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream paper-texture px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md bg-brand-warm-white border border-brand-linen p-10">
        <h1 className="font-serif text-4xl text-center mb-2">Rosewood Cottage</h1>
        <p className="overline text-center block mb-8">Keeper's Entrance</p>
        <div className="space-y-4">
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" className={cls} />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" className={cls} />
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full bg-brand-olive text-brand-cream py-4 font-sans uppercase tracking-[0.16em] text-xs hover:bg-brand-charcoal transition-colors">
            {loading ? "…" : "Enter"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("aw_token"));
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const [bulkRegenerating, setBulkRegenerating] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [payHealth, setPayHealth] = useState(null);
  const [syncingStripe, setSyncingStripe] = useState(false);
  const nav = useNavigate();

  const load = () => {
    adminStats().then(setStats).catch(() => setAuthed(false));
    getProducts().then(setProducts).catch(() => {});
    getJournal().then(setArticles).catch(() => {});
    adminOrders().then(setOrders).catch(() => {});
    adminSubscribers().then(setSubs).catch(() => {});
    adminMessages().then(setMsgs).catch(() => {});
    api.get("/admin/payments/health").then((r) => setPayHealth(r.data)).catch(() => setPayHealth(null));
  };
  useEffect(() => { if (authed) load(); }, [authed]);

  const logout = () => { localStorage.removeItem("aw_token"); setAuthed(false); };
  const delProduct = async (slug) => { await adminDeleteProduct(slug); toast.success("Removed"); load(); };
  const delArticle = async (slug) => { await adminDeleteArticle(slug); toast.success("Removed"); load(); };
  const uploadFile = async (slug, file) => {
    if (!file) return;
    setUploading(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/products/${slug}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded — buyers can now download it");
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(null); }
  };
  const syncStripe = async () => {
    setSyncingStripe(true);
    try {
      const res = await api.post("/admin/stripe/setup");
      toast.success(res.data.message || "Setting up Stripe products…");
    } catch { toast.error("Could not start Stripe setup"); }
    finally { setSyncingStripe(false); }
  };

  const pollRegen = (slug) => {
    const tick = async () => {
      try {
        const r = await api.get(`/admin/products/${slug}/regen-status`);
        setProducts((prev) => prev.map((p) => (p.slug === slug ? { ...p, ...r.data } : p)));
        if (r.data.regen_status === "running") {
          setTimeout(tick, 12000);
        } else {
          if (r.data.regen_status === "done") {
            toast.success(`"${slug}" ready — ${r.data.regen_recipe_count || 0} recipes`);
            load();
          } else if (r.data.regen_status === "failed") {
            toast.error(`Regeneration failed: ${r.data.regen_error || "unknown error"}`);
          }
        }
      } catch { /* keep last known state */ }
    };
    setTimeout(tick, 8000);
  };

  const pollAllRegen = () => {
    const tick = async () => {
      try {
        const list = await getProducts();
        setProducts(list);
        if (list.some((p) => p.regen_status === "running")) {
          setTimeout(tick, 15000);
        } else {
          const done = list.filter((p) => p.regen_status === "done").length;
          const failed = list.filter((p) => p.regen_status === "failed");
          if (failed.length) toast.error(`${failed.length} book(s) failed to regenerate`);
          else toast.success(`All ${done} books regenerated`);
        }
      } catch { /* keep last known state */ }
    };
    setTimeout(tick, 10000);
  };

  const regenerateAll = async () => {
    setBulkRegenerating(true);
    try {
      const res = await api.post(`/admin/products/regenerate-all`);
      toast.success(res.data.message || "Regenerating all books — watch the status here");
      setProducts((prev) => prev.map((p) => ({ ...p, regen_status: "running", regen_error: "" })));
      pollAllRegen();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start regeneration");
    }
    finally { setBulkRegenerating(false); }
  };

  const regeneratePdf = async (slug) => {
    setRegenerating(slug);
    try {
      const res = await api.post(`/admin/products/${slug}/regenerate`);
      toast.success(res.data.message || "Regenerating — watch the status here");
      setProducts((prev) => prev.map((p) => (p.slug === slug ? { ...p, regen_status: "running", regen_error: "" } : p)));
      pollRegen(slug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start regeneration");
    }
    finally { setRegenerating(null); }
  };
  const viewPdf = async (slug) => {
    setViewingPdf(slug);
    try {
      const res = await api.get(`/admin/products/${slug}/preview`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("No PDF available for this product yet"); }
    finally { setViewingPdf(null); }
  };
  const generatePost = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/admin/journal/generate");
      toast.success(`Published: "${res.data.title}"`);
      load();
    } catch { toast.error("Could not generate a post. Please try again."); }
    finally { setGenerating(false); }
  };
  const generateRecipe = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/admin/daily-recipes/generate");
      toast.success(`Recipe published: "${res.data.title}"`);
      load();
    } catch { toast.error("Could not generate a recipe. Please try again."); }
    finally { setGenerating(false); }
  };

  if (!authed) return <Login onIn={() => setAuthed(true)} />;

  const anyRunning = products.some((p) => p.regen_status === "running");
  const totalBooks = products.length;
  const finishedCount = products.filter((p) => p.regen_status === "done" || p.regen_status === "failed").length;
  const failedCount = products.filter((p) => p.regen_status === "failed").length;
  const runningBook = products.find((p) => p.regen_status === "running");

  const TABS = [["overview", "Overview"], ["products", "Products"], ["journal", "Journal"], ["orders", "Orders"], ["subscribers", "Subscribers"], ["messages", "Messages"]];
  const StatCard = ({ icon: Icon, label, value }) => (
    <div className="bg-brand-warm-white border border-brand-linen p-8">
      <Icon size={22} className="text-brand-copper mb-4" />
      <p className="font-serif text-4xl text-brand-charcoal">{value}</p>
      <p className="overline mt-2">{label}</p>
    </div>
  );

  const PaymentsCard = ({ h }) => {
    if (!h) return null;
    const live = h.declared_mode === "live";
    const ready = h.fully_ready;
    // status: green when live+ready, amber when test mode, red when live but not ready
    const status = ready ? "ready" : live ? "action" : "test";
    const theme = {
      ready: { bg: "bg-brand-olive text-brand-cream", border: "border-brand-olive", icon: CheckCircle2, label: "Live & ready", sub: "Real payments enabled" },
      action: { bg: "bg-[#8a3b2e] text-brand-cream", border: "border-[#8a3b2e]", icon: AlertTriangle, label: "Action needed", sub: "Live mode is not fully configured" },
      test: { bg: "bg-brand-warm-white text-brand-charcoal", border: "border-brand-gold", icon: CreditCard, label: "Test mode", sub: "No real charges yet — safe to preview" },
    }[status];
    const Ico = theme.icon;
    const Row = ({ ok, children }) => (
      <li className="flex items-start gap-2.5 font-body text-sm">
        {ok ? <CheckCircle2 size={16} className="text-brand-sage shrink-0 mt-0.5" />
            : <AlertTriangle size={16} className="text-[#b5651d] shrink-0 mt-0.5" />}
        <span className={status === "ready" ? "text-brand-cream/90" : "text-brand-body"}>{children}</span>
      </li>
    );
    const copyHook = () => { navigator.clipboard?.writeText(h.webhook_endpoint); toast.success("Webhook URL copied"); };
    const subText = status === "ready" ? "text-brand-cream/70" : "text-brand-muted";
    return (
      <div className={`border ${theme.border} sm:col-span-2 lg:col-span-3 overflow-hidden`} data-testid="admin-payments-health">
        <div className={`${theme.bg} px-8 py-6 flex items-center gap-4`}>
          <Ico size={30} strokeWidth={1.6} className="shrink-0" />
          <div className="flex-1">
            <p className="overline opacity-80 mb-0.5">Payments · Stripe</p>
            <p className="font-serif text-2xl leading-none" data-testid="payments-status-label">{theme.label}</p>
          </div>
          <span className="font-sans text-[0.66rem] uppercase tracking-[0.14em] opacity-80 hidden sm:block">{theme.sub}</span>
        </div>
        <div className="bg-brand-warm-white p-8 grid md:grid-cols-2 gap-x-10 gap-y-4">
          <ul className="space-y-3">
            <Row ok={h.mode_matches}>
              Mode: <strong className="uppercase">{h.declared_mode}</strong>
              {h.key_mode !== "unknown" && h.key_mode !== h.declared_mode && <span className="text-[#b5651d]"> (key is {h.key_mode})</span>}
            </Row>
            <Row ok={h.webhook_secret_configured}>
              Webhook secret {h.webhook_secret_configured ? "configured" : "missing"}
            </Row>
            <Row ok={h.prices_ok}>
              {h.prices_ok
                ? `All ${h.prices_checked} product prices resolve`
                : `${(h.missing_prices || []).length} price(s) missing in this Stripe mode`}
            </Row>
          </ul>
          <div className="space-y-3">
            {(h.missing_prices || []).length > 0 && (
              <p className="font-body text-sm text-[#8a3b2e]">
                Create these in your Stripe account (same lookup keys):<br />
                <span className="font-mono text-xs break-words">{h.missing_prices.join(", ")}</span>
              </p>
            )}
            {h.price_error && <p className="font-body text-sm text-[#8a3b2e]">Stripe error: {h.price_error}</p>}
            <div>
              <p className="overline mb-1.5">Webhook endpoint</p>
              <button onClick={copyHook} data-testid="copy-webhook-url"
                className="w-full text-left flex items-center gap-2 border border-brand-linen hover:border-brand-gold px-3 py-2.5 font-mono text-xs text-brand-body break-all transition-colors">
                <Copy size={14} className="text-brand-copper shrink-0" /> {h.webhook_endpoint}
              </button>
            </div>
            {status === "test" && (
              <p className="font-body text-xs text-brand-muted leading-relaxed">
                To go live: add your live Stripe keys + webhook secret and set <span className="font-mono">STRIPE_MODE=live</span> in your deployed app settings, then redeploy.
              </p>
            )}
            <button onClick={syncStripe} disabled={syncingStripe} data-testid="sync-stripe-products"
              className="w-full inline-flex items-center justify-center gap-2 border border-brand-gold text-brand-charcoal px-4 py-2.5 font-sans text-[0.66rem] uppercase tracking-[0.14em] hover:bg-brand-olive hover:text-brand-cream hover:border-brand-olive transition-colors disabled:opacity-60">
              <CreditCard size={14} /> {syncingStripe ? "Setting up…" : `Sync products to Stripe (${h.declared_mode})`}
            </button>
            <p className="font-body text-[0.68rem] text-brand-muted leading-relaxed">
              Creates all 17 products in your Stripe account for the current mode. Run this once after switching to live keys, then refresh.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-dashboard">
      <div className="border-b border-brand-linen bg-brand-warm-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
          <div>
            <h1 className="font-serif text-2xl">Rosewood Cottage</h1>
            <span className="overline text-[0.6rem]">Keeper's Study</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => nav("/")} className="font-sans text-xs uppercase tracking-[0.14em] text-brand-body hover:text-brand-charcoal">View site</button>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 font-sans text-xs uppercase tracking-[0.14em] text-brand-copper"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-10">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`font-sans text-[0.72rem] uppercase tracking-[0.14em] px-5 py-2 border transition-colors ${tab === k ? "bg-brand-olive text-brand-cream border-brand-olive" : "border-brand-linen text-brand-body hover:border-brand-gold"}`}>{l}</button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-overview">
            <PaymentsCard h={payHealth} />
            <StatCard icon={Coins} label="Revenue (paid)" value={`$${stats.revenue.toFixed(2)}`} />
            <StatCard icon={BookOpen} label="Products" value={stats.products} />
            <StatCard icon={Feather} label="Journal Articles" value={stats.articles} />
            <StatCard icon={Users} label="Subscribers" value={stats.subscribers} />
            <StatCard icon={Mail} label="Messages" value={stats.messages} />
            <StatCard icon={ShoppingBag} label="Paid Orders" value={stats.orders_paid} />
            <StatCard icon={Users} label="Members" value={stats.members ?? 0} />
            <StatCard icon={Feather} label="Daily Recipes" value={stats.recipes ?? 0} />
            <div className="bg-brand-warm-white border border-brand-linen p-8 flex flex-col justify-center gap-3">
              <span className="overline">Autopilot</span>
              <button onClick={generatePost} disabled={generating} data-testid="overview-generate-post"
                className="inline-flex items-center gap-2 justify-center bg-brand-olive text-brand-cream px-4 py-2.5 font-sans text-[0.66rem] uppercase tracking-[0.14em] hover:bg-brand-charcoal transition-colors disabled:opacity-60">
                <Sparkles size={13} /> {generating ? "Writing…" : "Weekly post now"}
              </button>
              <button onClick={generateRecipe} disabled={generating} data-testid="overview-generate-recipe"
                className="inline-flex items-center gap-2 justify-center border border-brand-gold text-brand-charcoal px-4 py-2.5 font-sans text-[0.66rem] uppercase tracking-[0.14em] hover:bg-brand-linen transition-colors disabled:opacity-60">
                <Sparkles size={13} /> {generating ? "Cooking…" : "Daily recipe now"}
              </button>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div className="space-y-3" data-testid="admin-products">
            <div className="bg-brand-warm-white border border-brand-linen p-5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="font-serif text-xl">Regenerate the whole library</h3>
                  <p className="text-sm text-brand-muted mt-1">
                    Rebuilds every ebook with fresh recipes, one at a time. Takes a while — watch each book's status below.
                  </p>
                </div>
                <button onClick={regenerateAll} disabled={bulkRegenerating || anyRunning} data-testid="regenerate-all"
                  className="inline-flex items-center gap-2 border border-brand-gold text-brand-charcoal px-5 py-2.5 font-sans text-[0.68rem] uppercase tracking-[0.14em] hover:bg-brand-linen transition-colors disabled:opacity-60">
                  <Sparkles size={14} /> {anyRunning ? "Regenerating…" : bulkRegenerating ? "Starting…" : "Regenerate all books"}
                </button>
              </div>
              {anyRunning && (
                <div className="mt-4" data-testid="regen-progress">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-sans text-[0.66rem] uppercase tracking-[0.14em] text-brand-olive">
                      {finishedCount} of {totalBooks} done{runningBook ? ` · now: ${runningBook.title}` : ""}
                    </span>
                    <span className="font-sans text-[0.66rem] uppercase tracking-[0.14em] text-brand-muted">
                      {Math.round((finishedCount / totalBooks) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 w-full bg-brand-linen overflow-hidden rounded-full">
                    <div className="h-full bg-brand-gold transition-all duration-500 ease-out"
                      style={{ width: `${(finishedCount / totalBooks) * 100}%` }} />
                  </div>
                  {failedCount > 0 && (
                    <p className="text-xs text-brand-copper mt-1.5">{failedCount} failed — click that book's Regenerate to retry.</p>
                  )}
                </div>
              )}
            </div>
            {products.map((p) => (
              <div key={p.slug} className="bg-brand-warm-white border border-brand-linen p-5 flex flex-wrap items-center gap-5">
                <img src={p.cover} alt="" className="w-16 h-16 object-cover" />
                <div className="flex-1 min-w-[180px]">
                  <h3 className="font-serif text-xl">{p.title}</h3>
                  <p className="text-sm text-brand-muted">{p.category} · ${p.price.toFixed(2)}</p>
                  <p className="text-xs mt-1">
                    {p.has_file
                      ? <span className="text-brand-olive">✓ File uploaded{p.download_filename ? ` · ${p.download_filename}` : ""}</span>
                      : <span className="text-brand-muted">No file yet — buyers see a "prepared shortly" note</span>}
                  </p>
                  {p.regen_status && (
                    <p className="text-xs mt-1" data-testid={`regen-status-${p.slug}`}>
                      {p.regen_status === "running" && <span className="text-brand-gold">⟳ Generating recipes… (a few minutes)</span>}
                      {p.regen_status === "done" && <span className="text-brand-olive">✓ Regenerated · {p.regen_recipe_count || 0} recipes</span>}
                      {p.regen_status === "failed" && <span className="text-brand-copper">✕ Failed: {p.regen_error}</span>}
                    </p>
                  )}
                </div>
                <label className="cursor-pointer inline-flex items-center gap-2 border border-brand-linen hover:border-brand-gold px-4 py-2 font-sans text-[0.68rem] uppercase tracking-[0.14em] text-brand-body transition-colors" data-testid={`upload-file-${p.slug}`}>
                  <Upload size={14} /> {uploading === p.slug ? "Uploading…" : "Upload PDF"}
                  <input type="file" accept=".pdf,application/pdf" className="hidden"
                    onChange={(e) => uploadFile(p.slug, e.target.files[0])} disabled={uploading === p.slug} />
                </label>
                <button onClick={() => regeneratePdf(p.slug)} disabled={regenerating === p.slug || p.regen_status === "running"} data-testid={`regenerate-${p.slug}`}
                  className="inline-flex items-center gap-2 border border-brand-linen hover:border-brand-gold px-4 py-2 font-sans text-[0.68rem] uppercase tracking-[0.14em] text-brand-body transition-colors disabled:opacity-60">
                  <Sparkles size={14} /> {p.regen_status === "running" ? "Generating…" : regenerating === p.slug ? "Starting…" : "Regenerate"}
                </button>
                <button onClick={() => viewPdf(p.slug)} disabled={viewingPdf === p.slug} data-testid={`view-pdf-${p.slug}`}
                  className="inline-flex items-center gap-2 border border-brand-linen hover:border-brand-gold px-4 py-2 font-sans text-[0.68rem] uppercase tracking-[0.14em] text-brand-body transition-colors disabled:opacity-60">
                  <BookOpen size={14} /> {viewingPdf === p.slug ? "Opening…" : "View PDF"}
                </button>
                <button onClick={() => delProduct(p.slug)} data-testid={`del-product-${p.slug}`} className="text-brand-copper hover:text-destructive"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "journal" && (
          <div data-testid="admin-journal">
            <div className="flex justify-end mb-5">
              <button onClick={generatePost} disabled={generating} data-testid="generate-post-btn"
                className="inline-flex items-center gap-2 bg-brand-olive text-brand-cream px-6 py-3 font-sans text-[0.7rem] uppercase tracking-[0.16em] hover:bg-brand-charcoal transition-colors disabled:opacity-60">
                <Sparkles size={15} /> {generating ? "Agnes is writing…" : "Generate this week's post now"}
              </button>
            </div>
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.slug} className="bg-brand-warm-white border border-brand-linen p-5 flex items-center gap-5">
                  <img src={a.cover} alt="" className="w-16 h-16 object-cover" />
                  <div className="flex-1">
                    <h3 className="font-serif text-xl">{a.title}</h3>
                    <p className="text-sm text-brand-muted">{a.category}{a.auto_generated ? " · auto-written" : ""}</p>
                  </div>
                  <button onClick={() => delArticle(a.slug)} data-testid={`del-article-${a.slug}`} className="text-brand-copper hover:text-destructive"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders-view">
            <div className="bg-brand-linen/40 border border-brand-linen p-4 mb-5 flex flex-wrap items-center gap-x-8 gap-y-2">
              <p className="font-body text-sm text-brand-body">
                Orders are processed securely by <strong>Stripe</strong> (not Shopify). This list mirrors your payments.
                For payouts, receipts and refunds, open your{" "}
                <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noopener noreferrer" className="text-brand-copper underline" data-testid="stripe-dashboard-link">Stripe Dashboard</a>.
              </p>
              <div className="flex items-center gap-6 ml-auto">
                <div className="text-center"><p className="font-serif text-2xl text-brand-olive" data-testid="orders-paid-count">{orders.filter((o) => o.payment_status === "paid").length}</p><p className="overline text-[0.56rem]">Paid</p></div>
                <div className="text-center"><p className="font-serif text-2xl text-brand-muted">{orders.filter((o) => o.payment_status !== "paid").length}</p><p className="overline text-[0.56rem]">Pending</p></div>
                <div className="text-center"><p className="font-serif text-2xl text-brand-charcoal">${(orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0) / 100).toFixed(2)}</p><p className="overline text-[0.56rem]">Collected</p></div>
              </div>
            </div>
            <div className="bg-brand-warm-white border border-brand-linen divide-y divide-brand-linen" data-testid="admin-orders">
              {orders.length === 0 && <p className="p-6 font-body text-brand-muted">No orders yet. When a customer buys, it appears here and in your Stripe Dashboard.</p>}
              {orders.map((o, i) => {
                const title = o.product_title || o.lookup_key || "Order";
                const isSub = o.is_subscription;
                const when = o.created_at ? new Date(o.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
                const paid = o.payment_status === "paid";
                const name = o.customer_name;
                return (
                  <div key={i} className="p-5 flex justify-between items-start gap-4" data-testid={`order-row-${i}`}>
                    <div className="min-w-0">
                      <p className="font-body text-brand-charcoal flex items-center gap-2 flex-wrap">
                        {title}
                        <span className="text-[0.56rem] uppercase tracking-[0.12em] border border-brand-linen px-1.5 py-0.5 text-brand-muted">{isSub ? "Subscription" : "Digital"}</span>
                      </p>
                      <p className="text-sm text-brand-charcoal mt-1" data-testid={`order-customer-${i}`}>
                        {name ? <span className="font-medium">{name}</span> : <span className="text-brand-muted italic">Guest</span>}
                        {o.email ? <span className="text-brand-muted"> · {o.email}</span> : <span className="text-brand-muted"> · no email on file</span>}
                      </p>
                      <p className="text-xs text-brand-muted mt-1">
                        {o.order_no && <span className="font-mono">{o.order_no}</span>}{o.order_no && " · "}{when}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-serif text-lg">${((o.amount || 0) / 100).toFixed(2)}</p>
                      <span className={`inline-block text-[0.6rem] uppercase tracking-[0.1em] px-2 py-0.5 mt-1 ${paid ? "bg-brand-olive text-brand-cream" : "bg-brand-linen text-brand-muted"}`} data-testid={`order-status-${i}`}>{o.payment_status || "pending"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "subscribers" && (
          <div className="bg-brand-warm-white border border-brand-linen divide-y divide-brand-linen" data-testid="admin-subscribers">
            {subs.length === 0 && <p className="p-6 font-body text-brand-muted">No subscribers yet.</p>}
            {subs.map((s, i) => <div key={i} className="p-5 flex justify-between"><span className="font-body">{s.email}</span><span className="text-xs text-brand-muted">{new Date(s.created_at).toLocaleDateString()}</span></div>)}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3" data-testid="admin-messages">
            {msgs.length === 0 && <p className="font-body text-brand-muted">No messages yet.</p>}
            {msgs.map((m, i) => (
              <div key={i} className="bg-brand-warm-white border border-brand-linen p-6">
                <div className="flex justify-between"><h3 className="font-serif text-xl">{m.name}</h3><span className="text-xs text-brand-muted">{new Date(m.created_at).toLocaleDateString()}</span></div>
                <p className="text-sm text-brand-copper">{m.email} · {m.subject}</p>
                <p className="font-body text-brand-body mt-3 leading-relaxed">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
